import React, { useEffect, useState } from "react";
import { 
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, 
  Container, Chip, Tooltip, Snackbar, Alert, Menu, MenuItem, Select, FormControl, InputLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import ComplianceChecksManager from "./ComplianceChecksManager";

export default function ManageAccounts({ onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAssignUserDialog, setOpenAssignUserDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({ name: "", address: "", email: "" });
  const [editAccount, setEditAccount] = useState({ name: "", address: "", email: "" });
  const [assignUserData, setAssignUserData] = useState({ user_id: "", account_id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('accounts'); // 'accounts' or 'compliance-checks'
  const [complianceCheckAccount, setComplianceCheckAccount] = useState(null);

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
  }, []);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/accounts/", {
        credentials: "include"
      });
      if (response.ok) {
        const accountsData = await response.json();
        setAccounts(accountsData);
      } else {
        showSnackbar("Error fetching accounts", "error");
      }
    } catch (error) {
      showSnackbar("Error fetching accounts", "error");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/users/", {
        credentials: "include"
      });
      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      } else {
        showSnackbar("Error fetching users", "error");
      }
    } catch (error) {
      showSnackbar("Error fetching users", "error");
    }
  };

  const createAccount = async () => {
    if (!newAccount.name.trim()) {
      showSnackbar("Account name is required", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/accounts/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newAccount)
      });

      if (response.ok) {
        const createdAccount = await response.json();
        setAccounts([...accounts, createdAccount]);
        setNewAccount({ name: "", address: "", email: "" });
        setOpenCreateDialog(false);
        showSnackbar("Account created successfully", "success");
      } else {
        const error = await response.json();
        showSnackbar(error.detail || "Error creating account", "error");
      }
    } catch (error) {
      showSnackbar("Error creating account", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateAccount = async () => {
    if (!editAccount.name.trim()) {
      showSnackbar("Account name is required", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/accounts/${selectedAccount.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editAccount)
      });

      if (response.ok) {
        const updatedAccount = await response.json();
        setAccounts(accounts.map(acc => acc.id === selectedAccount.id ? updatedAccount : acc));
        setOpenEditDialog(false);
        setSelectedAccount(null);
        showSnackbar("Account updated successfully", "success");
      } else {
        const error = await response.json();
        showSnackbar(error.detail || "Error updating account", "error");
      }
    } catch (error) {
      showSnackbar("Error updating account", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to delete this account? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/accounts/${accountId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setAccounts(accounts.filter(acc => acc.id !== accountId));
        showSnackbar("Account deleted successfully", "success");
      } else {
        const error = await response.json();
        showSnackbar(error.detail || "Error deleting account", "error");
      }
    } catch (error) {
      showSnackbar("Error deleting account", "error");
    }
  };

  const assignUserToAccount = async () => {
    if (!assignUserData.user_id) {
      showSnackbar("Please select a user", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${assignUserData.user_id}/account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: assignUserData.user_id, account_id: assignUserData.account_id })
      });

      if (response.ok) {
        // Refresh users to show updated account assignments
        await fetchUsers();
        setOpenAssignUserDialog(false);
        setAssignUserData({ user_id: "", account_id: null });
        showSnackbar("User account assignment updated successfully", "success");
      } else {
        const error = await response.json();
        showSnackbar(error.detail || "Error updating user account assignment", "error");
      }
    } catch (error) {
      showSnackbar("Error updating user account assignment", "error");
    } finally {
      setLoading(false);
    }
  };

  const openEditAccountDialog = (account) => {
    setSelectedAccount(account);
    setEditAccount({ name: account.name, address: account.address || "", email: account.email || "" });
    setOpenEditDialog(true);
  };

  const getUsersForAccount = (accountId) => {
    return users.filter(user => user.account_id === accountId);
  };

  const getUnassignedUsers = () => {
    return users.filter(user => !user.account_id);
  };

  const showComplianceChecks = (account) => {
    setComplianceCheckAccount(account);
    setViewMode('compliance-checks');
  };

  const backToAccounts = () => {
    setViewMode('accounts');
    setComplianceCheckAccount(null);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={viewMode === 'compliance-checks' ? backToAccounts : onClose} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {viewMode === 'compliance-checks' ? `Compliance Checks - ${complianceCheckAccount?.name}` : 'Manage Accounts'}
          </Typography>
        </Box>

        {viewMode === 'accounts' ? (
          <>
            <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
              >
                Create Account
              </Button>
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenAssignUserDialog(true)}
              >
                Assign User to Account
              </Button>
            </Box>

            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Users</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account) => {
                    const accountUsers = getUsersForAccount(account.id);
                    return (
                      <TableRow key={account.id}>
                        <TableCell>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {account.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {account.address || "No address"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {account.email || "No email"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {accountUsers.length === 0 ? (
                              <Chip label="No users" size="small" variant="outlined" />
                            ) : (
                              accountUsers.map((user) => (
                                <Chip
                                  key={user.id}
                                  label={user.username}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ))
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {new Date(account.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Compliance checks">
                              <IconButton
                                color="secondary"
                                onClick={() => showComplianceChecks(account)}
                                size="small"
                              >
                                <SettingsIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit account">
                              <IconButton
                                color="primary"
                                onClick={() => openEditAccountDialog(account)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete account">
                              <IconButton
                                color="error"
                                onClick={() => deleteAccount(account.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {accounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          No accounts found. Create your first account to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        ) : (
          <ComplianceChecksManager 
            accountId={complianceCheckAccount?.id}
            accountName={complianceCheckAccount?.name}
          />
        )}
      </Box>

      {/* Only show dialogs in accounts view */}
      {viewMode === 'accounts' && (
        <>
          {/* Create Account Dialog */}
          <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Account Name *"
            fullWidth
            variant="outlined"
            value={newAccount.name}
            onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newAccount.address}
            onChange={(e) => setNewAccount({ ...newAccount, address: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newAccount.email}
            onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button onClick={createAccount} variant="contained" disabled={loading}>
            {loading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Account Name *"
            fullWidth
            variant="outlined"
            value={editAccount.name}
            onChange={(e) => setEditAccount({ ...editAccount, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={editAccount.address}
            onChange={(e) => setEditAccount({ ...editAccount, address: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={editAccount.email}
            onChange={(e) => setEditAccount({ ...editAccount, email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={updateAccount} variant="contained" disabled={loading}>
            {loading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User to Account Dialog */}
      <Dialog open={openAssignUserDialog} onClose={() => setOpenAssignUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to Account</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={assignUserData.user_id}
              label="User"
              onChange={(e) => setAssignUserData({ ...assignUserData, user_id: e.target.value })}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box>
                    <Typography variant="body1">{user.name || user.username}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {user.username} {user.account_name ? `(Currently: ${user.account_name})` : "(No account)"}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Account</InputLabel>
            <Select
              value={assignUserData.account_id || ""}
              label="Account"
              onChange={(e) => setAssignUserData({ ...assignUserData, account_id: e.target.value || null })}
            >
              <MenuItem value="">
                <em>No Account (Remove association)</em>
              </MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignUserDialog(false)}>Cancel</Button>
          <Button onClick={assignUserToAccount} variant="contained" disabled={loading}>
            {loading ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </>
      )}
    </Container>
  );
}